import type { PriceBoardRow } from 'api/types';

/**
 * Price Board arithmetic (crs-doc DineOps/DineOps-Item-Pricing.md D1, D2).
 *
 * The board keeps unsaved edits as a map of changed cells. A cell is one dish in
 * one column: the Menu column (the menu price) or an outlet (that outlet's own
 * price, where null follows the menu price). Nothing here talks to the server.
 */

/** 'menu' or an outlet id. */
export type BoardColumn = string;
export const MENU: BoardColumn = 'menu';

export interface PendingChange {
  itemId: string;
  /** null = the Menu column. */
  outletId: string | null;
  itemName: string;
  /** "Menu" or the outlet's name. */
  column: string;
  /** The saved value. Null on an outlet cell = follows the menu price. */
  before: number | null;
  /** The new value. Null on an outlet cell = follow the menu price again. */
  after: number | null;
  rowVersion: number;
}

export type PendingMap = Map<string, PendingChange>;

export const cellKey = (itemId: string, column: BoardColumn) => `${itemId}|${column}`;

/** A row the board prices directly: not a dish with sizes (its sizes are priced) and not open-priced. */
export const isPricedRow = (row: PriceBoardRow) => row.sizeCount === 0 && !row.isOpenPrice;

/** Money to 2 places, half away from zero, as the server rounds. */
export const to2 = (n: number) => Math.sign(n) * Math.round(Math.abs(n) * 100 + Number.EPSILON) / 100;

/** To the nearest step (1, 5, 10); 0 = 2 decimal places. */
export const roundTo = (n: number, step: number) => (step > 0 ? to2(Math.round(n / step) * step) : to2(n));

/** The saved value of a cell; undefined when the outlet does not sell the dish. */
export const savedValue = (row: PriceBoardRow, column: BoardColumn): number | null | undefined => {
  if (column === MENU) return row.menuPrice;
  const c = row.cells.find((x) => x.outletId === column);
  if (!c || !c.sold) return undefined;
  return c.price ?? null;
};

export const savedVersion = (row: PriceBoardRow, column: BoardColumn): number =>
  column === MENU ? row.menuRowVersion : (row.cells.find((x) => x.outletId === column)?.rowVersion ?? 0);

/** What the board shows in a cell: the unsaved value when there is one, else the saved one. */
export const boardValue = (row: PriceBoardRow, column: BoardColumn, pending: PendingMap): number | null | undefined => {
  const p = pending.get(cellKey(row.itemId, column));
  return p ? p.after : savedValue(row, column);
};

/** The menu price the row's outlet cells follow, unsaved edits included. */
export const boardMenuPrice = (row: PriceBoardRow, pending: PendingMap): number =>
  (boardValue(row, MENU, pending) as number | null) ?? row.menuPrice;

/**
 * Sets one cell's unsaved value. Returns a new map. Setting a cell back to its
 * saved value removes the pending change.
 */
export const setCell = (
  pending: PendingMap, row: PriceBoardRow, column: BoardColumn, columnName: string, after: number | null,
): PendingMap => {
  const saved = savedValue(row, column);
  if (saved === undefined) return pending;
  const next = new Map(pending);
  const key = cellKey(row.itemId, column);
  if (after === saved) next.delete(key);
  else next.set(key, {
    itemId: row.itemId, outletId: column === MENU ? null : column, itemName: row.itemName, column: columnName,
    before: saved, after, rowVersion: savedVersion(row, column),
  });
  return next;
};

export type BulkOperation = 'set' | 'percent' | 'amount' | 'round';

export interface BulkSpec {
  operation: BulkOperation;
  /** The amount, percentage or new price; ignored for 'round'. Negative lowers. */
  value: number;
  /** 0 = 2 decimal places; else the nearest 1, 5 or 10. Required (>0) for 'round'. */
  rounding: number;
}

export interface BulkPreviewLine {
  key: string;
  row: PriceBoardRow;
  column: BoardColumn;
  columnName: string;
  /** What the board shows now (unsaved edits included); null = follows the menu price. */
  current: number | null;
  /** The price the cell charges now: its own, else the menu price. */
  currentCharged: number;
  after: number;
}

export interface BulkSkip {
  itemName: string;
  columnName: string;
  reason: string;
}

/**
 * The cells a bulk change touches and their new prices.
 *
 * - Dishes with sizes and open-priced dishes are left out (their rows are not priced).
 * - A cell whose outlet does not sell the dish is left out.
 * - An outlet cell that follows the menu price stays following it when the Menu
 *   column is changed too: it gets the new menu price without a price of its own.
 *   When the Menu column is not changed, the change gives it a price of its own
 *   (except 'round', which leaves it following the menu).
 * - A result below zero is refused for that cell.
 */
export const planBulk = (
  rows: PriceBoardRow[], columns: { id: BoardColumn; name: string }[], spec: BulkSpec, pending: PendingMap,
): { lines: BulkPreviewLine[]; skipped: BulkSkip[] } => {
  const lines: BulkPreviewLine[] = [];
  const skipped: BulkSkip[] = [];
  const menuChanges = columns.some((c) => c.id === MENU);

  for (const row of rows) {
    if (!isPricedRow(row)) continue;
    const menuNow = boardMenuPrice(row, pending);
    for (const col of columns) {
      const current = boardValue(row, col.id, pending);
      if (current === undefined) continue; // not sold here
      const follows = col.id !== MENU && current === null;
      if (follows && (menuChanges || spec.operation === 'round')) continue;

      const base = current ?? menuNow;
      let next: number;
      switch (spec.operation) {
        case 'set': next = spec.value; break;
        case 'percent': next = base * (1 + spec.value / 100); break;
        case 'amount': next = base + spec.value; break;
        default: next = base; break;
      }
      next = roundTo(next, spec.rounding);
      if (next < 0) { skipped.push({ itemName: row.itemName, columnName: col.name, reason: 'would be below zero' }); continue; }
      if (current !== null && next === current) continue;
      lines.push({ key: cellKey(row.itemId, col.id), row, column: col.id, columnName: col.name, current, currentCharged: base, after: next });
    }
  }
  return { lines, skipped };
};

/** Puts a bulk plan on the board as unsaved changes. */
export const applyPlan = (pending: PendingMap, lines: BulkPreviewLine[]): PendingMap =>
  lines.reduce((acc, l) => setCell(acc, l.row, l.column, l.columnName, l.after), pending);

/** Parses what a user typed in a cell: '' = empty; a number with up to 2 decimals; otherwise invalid. */
export const parsePrice = (text: string): { ok: true; value: number | null } | { ok: false } => {
  const t = text.replace(/,/g, '').trim();
  if (t === '') return { ok: true, value: null };
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return { ok: false };
  return { ok: true, value: Number(t) };
};

export const money = (n: number | null | undefined) =>
  n == null ? '' : n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Inline look for the Price Board dialogs' change tables (the global .table style is not loaded here). */
export const changeTable = {
  table: { width: '100%', borderCollapse: 'collapse' as const, margin: 0, fontSize: 13 },
  head: { position: 'sticky' as const, top: 0, background: 'var(--bg-secondary)', zIndex: 1 },
  th: { padding: '6px 10px', textAlign: 'left' as const, fontWeight: 600, borderBottom: '1px solid var(--border-color)' },
  td: { padding: '5px 10px', borderBottom: '1px solid var(--border-color)' },
};
